import React, { useState, useEffect } from 'react';
import ProductEditItem from './ProductEditItem';
import ProductEditForm from './ProductEditForm';
import './ProductEditList.css';
import api, { getProducts, updateProduct, syncProductsWithSqlServer, generateProductDescriptionAI, bulkGenerateAIDescriptions, bulkUpdateProductDescriptions } from '../../services/api';

const ProductEditList = () => {
  // State para edição em massa
  const [isBulkMode, setIsBulkMode] = useState(false);
  const [selectedProductIds, setSelectedProductIds] = useState(new Set());
  const [editableDescriptions, setEditableDescriptions] = useState(new Map());
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [message, setMessage] = useState({ text: '', type: '' });
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalProducts, setTotalProducts] = useState(0);
  const [editingProduct, setEditingProduct] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [generatingAIDescriptions, setGeneratingAIDescriptions] = useState(false);

  useEffect(() => {
    fetchProducts(currentPage, searchTerm); // Chama com os valores de estado atuais
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Executa apenas na montagem inicial

  useEffect(() => {
    if (products.length > 0) {
      const results = products.filter(product =>
        product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.code.toString().includes(searchTerm)
      );
      setFilteredProducts(results);
    }
  }, [searchTerm, products]);

  const fetchProducts = async (page = currentPage, search = searchTerm) => { 
    try {
      setLoading(true);
      setError(null);
      setMessage({ text: `Carregando produtos da página ${page}...`, type: 'info' });
      console.log(`Tentando carregar produtos usando o serviço api (página: ${page}, limite: 20)...`);

      // Limpar seleção ao mudar de página
      if (page !== currentPage) {
        setSelectedProductIds(new Set());
      }

      // Utiliza a função getProducts do apiService
      const response = await getProducts(page, 20, null, search);

      if (response && response.products) {
        setProducts(response.products);

        // Inicializar descrições editáveis para o modo de edição em massa com os produtos atuais
        const initialDescriptions = new Map();
        response.products.forEach(p => {
          if (p.id !== undefined && p.description !== undefined) {
            initialDescriptions.set(p.id, p.description);
          }
        });
        setEditableDescriptions(initialDescriptions);

        if (response.pagination) {
          setCurrentPage(response.pagination.page);
          setTotalPages(response.pagination.totalPages);
          setTotalProducts(response.pagination.total);
        } else {
          setTotalPages(Math.ceil(response.products.length / 20));
          setTotalProducts(response.products.length);
        }
        
        // Limpar mensagem de carregamento
        setMessage({ text: '', type: '' });
      } else {
        setProducts([]);
        setTotalPages(0);
        setTotalProducts(0);
        setMessage({ text: 'Nenhum produto encontrado', type: 'info' });
      }
    } catch (error) {
      console.error('Erro crítico ao carregar produtos:', error);
      setError(error.message || 'Falha ao carregar produtos.');
      setProducts([]);
      setTotalPages(0);
      setTotalProducts(0);
      setMessage({ text: `Erro ao carregar produtos: ${error.message || 'Falha desconhecida'}`, type: 'error' });
    } finally {
      setLoading(false);
      // Limpar mensagem após 5 segundos se for uma mensagem de sucesso
      if (!error) {
        setTimeout(() => setMessage({ text: '', type: '' }), 5000);
      }
    }
  };

  const handleUpdateProduct = async (updatedProduct) => {
    try {
      console.log('Iniciando processo de atualização do produto:', updatedProduct);
      
      // Verificar se o produto tem todos os campos necessários
      if (!updatedProduct.code) {
        console.error('Produto sem código não pode ser atualizado');
        setMessage({
          text: 'Erro: Produto sem código não pode ser atualizado',
          type: 'error'
        });
        return;
      }
      
      // Preparar o produto para atualização
      const productToUpdate = {
        ...updatedProduct,
        code: updatedProduct.code.toString(),
        name: updatedProduct.name || '',
        price: parseFloat(updatedProduct.price) || 0,
        salePrice: parseFloat(updatedProduct.salePrice || 0),
        stock: parseInt(updatedProduct.stock) || 0,
        active: updatedProduct.active === true,
        description: updatedProduct.description || '',
        tag: updatedProduct.tag || '',
        category: updatedProduct.category || ''
      };
      
      console.log('Produto preparado para atualização:', productToUpdate);
      
      // Usar a função updateProduct para atualizar o produto
      const result = await updateProduct(productToUpdate);
      console.log('Resultado da atualização:', result);
      
      // Atualiza o produto na lista local
      setProducts(products.map(p => 
        p.code === productToUpdate.code ? productToUpdate : p
      ));
      
      setMessage({
        text: 'Produto atualizado com sucesso!',
        type: 'success'
      });
      
      // Limpa a mensagem após 3 segundos
      setTimeout(() => {
        setMessage({ text: '', type: '' });
      }, 3000);

      // Fecha o formulário de edição
      setEditingProduct(null);
      
      // Recarregar a lista de produtos para garantir que as alterações foram aplicadas
      await fetchProducts();
      console.log('Lista de produtos recarregada após atualização');
    } catch (error) {
      console.error('Erro ao atualizar produto:', error);
      setMessage({
        text: `Erro ao atualizar produto: ${error.message || 'Tente novamente'}`,
        type: 'error'
      });
    }
  };

  const handleEditClick = (product) => {
    console.log('Iniciando edição do produto:', product);
    setEditingProduct(product);
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    setCurrentPage(1); // Resetar para a primeira página ao buscar
    fetchProducts(1, searchTerm);
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      // setCurrentPage(newPage); // fetchProducts já atualiza via setPagination
      fetchProducts(newPage, searchTerm);
    }
  };

  const handleCancelEdit = () => {
    setEditingProduct(null);
  };

  const handleGenerateAIDescriptionsForSelected = async () => {
    if (selectedProductIds.size === 0) {
      setMessage({ text: 'Nenhum produto selecionado para gerar descrições.', type: 'info' });
      return;
    }

    setGeneratingAIDescriptions(true);
    setMessage({ text: 'Gerando descrições com IA para produtos selecionados...', type: 'info' });
    
    try {
      // Preparar dados dos produtos selecionados
      const selectedProducts = [];
      
      for (const productId of selectedProductIds) {
        const product = products.find(p => p.id === productId);
        if (product) {
          selectedProducts.push({
            code: product.code,
            name: product.name,
            category: product.category || 'Não especificada',
            tag: product.tag || ''
          });
        }
      }
      
      // Processar em lotes de 5 produtos para evitar timeout
      const BATCH_SIZE = 5;
      const totalProducts = selectedProducts.length;
      const newDescriptions = new Map(editableDescriptions); // Copiar descrições existentes
      let successCount = 0;
      let errorCount = 0;
      let processedCount = 0;
      
      // Processar os produtos em lotes
      for (let i = 0; i < selectedProducts.length; i += BATCH_SIZE) {
        const batch = selectedProducts.slice(i, i + BATCH_SIZE);
        processedCount += batch.length;
        
        // Atualizar mensagem com progresso
        setMessage({ 
          text: `Gerando descrições com IA... ${processedCount}/${totalProducts} produtos processados.`, 
          type: 'info' 
        });
        
        console.log(`[AI_GEN_BULK]: Processando lote ${i/BATCH_SIZE + 1} com ${batch.length} produtos (${processedCount}/${totalProducts})`);
        
        try {
          // Chamar o endpoint para o lote atual
          const response = await bulkGenerateAIDescriptions(batch);
          
          if (response && response.results) {
            // Processar os resultados do lote
            response.results.forEach(result => {
              const productId = products.find(p => p.code === result.code)?.id;
              
              if (productId && result.status === 'success' && result.description) {
                newDescriptions.set(productId, result.description);
                successCount++;
              } else {
                console.warn(`[AI_GEN_BULK]: Problema com produto ${result.code}:`, result);
                errorCount++;
              }
            });
            
            // Atualizar as descrições no estado após cada lote
            setEditableDescriptions(new Map(newDescriptions));
          }
        } catch (batchError) {
          console.error(`[AI_GEN_BULK]: Erro no lote ${i/BATCH_SIZE + 1}:`, batchError);
          errorCount += batch.length;
        }
      }
      
      // Mensagem final após processar todos os lotes
      let finalMessage = `Geração de descrições concluída. ${successCount} gerada(s) com sucesso.`;
      if (errorCount > 0) {
        finalMessage += ` ${errorCount} falha(s).`;
      }
      
      setMessage({ 
        text: finalMessage, 
        type: errorCount > 0 && successCount === 0 ? 'error' : (errorCount > 0 ? 'warning' : 'success') 
      });
    } catch (error) {
      console.error('[AI_GEN_BULK]: Erro ao gerar descrições em massa:', error);
      setMessage({ 
        text: `Erro ao gerar descrições: ${error.message || 'Erro desconhecido'}`, 
        type: 'error' 
      });
    } finally {
      setGeneratingAIDescriptions(false);
      setTimeout(() => setMessage({ text: '', type: '' }), 7000);
    }
  };

  // Função para sincronizar com o SQL Server
  const handleSyncWithSqlServer = async () => {
    setSyncing(true);
    setMessage({ text: '', type: '' });
    console.log('Tentando sincronizar produtos com o SQL Server usando o serviço api...');

    try {
      // Utiliza a função syncProductsWithSqlServer do apiService
      const result = await syncProductsWithSqlServer();

      if (result && result.success) {
        console.log('Sincronização bem-sucedida:', result.message);
        setMessage({
          text: result.message || 'Produtos sincronizados com sucesso! Recarregando lista...',
          type: 'success'
        });
        await fetchProducts(); // Recarrega os produtos após a sincronização
      } else {
        console.error('Falha na sincronização:', result ? result.message : 'Resposta inválida do servidor');
        setMessage({
          text: `Erro ao sincronizar: ${result ? result.message : 'Resposta inválida do servidor'}`,
          type: 'error'
        });
      }
    } catch (error) {
      console.error('Erro crítico ao sincronizar com SQL Server:', error);
      setMessage({
        text: `Erro crítico ao sincronizar: ${error.message || 'Falha na comunicação com o servidor'}`,
        type: 'error'
      });
    } finally {
      setSyncing(false);
      setTimeout(() => {
        setMessage({ text: '', type: '' });
      }, 5000);
    }
  };

  const toggleBulkMode = () => {
    setIsBulkMode(!isBulkMode);
    setSelectedProductIds(new Set()); // Limpar seleções ao sair do modo de edição em massa
    // As descrições editáveis são mantidas, mas só aplicadas se salvas
  };

  const handleProductSelectToggle = (productId) => {
    setSelectedProductIds(prevSelectedIds => {
      const newSelectedIds = new Set(prevSelectedIds);
      if (newSelectedIds.has(productId)) {
        newSelectedIds.delete(productId);
      } else {
        newSelectedIds.add(productId);
      }
      return newSelectedIds;
    });
  };

  // Função para selecionar/desselecionar todos os produtos
  const handleSelectAllToggle = () => {
    if (selectedProductIds.size === filteredProducts.length) {
      // Se todos estão selecionados, desmarca todos
      setSelectedProductIds(new Set());
    } else {
      // Caso contrário, seleciona todos os produtos filtrados
      const allIds = new Set(filteredProducts.map(product => product.id));
      setSelectedProductIds(allIds);
    }
  };

  const handleDescriptionChange = (productId, newDescription) => {
    setEditableDescriptions(prevDescriptions => {
      const newDescriptions = new Map(prevDescriptions);
      newDescriptions.set(productId, newDescription);
      return newDescriptions;
    });
  };

  const handleSaveBulkDescriptions = async () => {
    console.log('[BULK_SAVE_DEBUG]: handleSaveBulkDescriptions iniciada.');
    console.log('[BULK_SAVE_DEBUG]: Verificando selectedProductIds.size:', selectedProductIds.size);
    if (selectedProductIds.size === 0) {
      setMessage({ text: 'Nenhum produto selecionado para atualizar.', type: 'info' });
      console.log('[BULK_SAVE_DEBUG]: Nenhum produto selecionado. Retornando.');
      return;
    }

    console.log('[BULK_SAVE_DEBUG]: selectedProductIds.size > 0. Prosseguindo para mapear productsToUpdate.');
    const productsToUpdate = Array.from(selectedProductIds).map(id => ({
      id: id, // Certifique-se que este 'id' é o ID numérico do banco
      new_description: editableDescriptions.get(id) || '' // Pega a descrição do estado editável
    }));

    console.log('[BULK_SAVE_DEBUG]: productsToUpdate mapeado:', productsToUpdate);
    console.log('Salvando descrições em massa:', productsToUpdate);
    console.log('[BULK_SAVE_DEBUG]: Antes de setLoading(true).');
    setLoading(true); // Reutilizando o estado de loading principal
    try {
      console.log('ProductEditList: Enviando para bulkUpdateProductDescriptions:', productsToUpdate);
      const response = await bulkUpdateProductDescriptions(productsToUpdate);
      setMessage({ text: response.message || 'Descrições atualizadas com sucesso!', type: 'success' });
      await fetchProducts(); // Recarregar produtos para ver as atualizações e resetar descrições editáveis
      setSelectedProductIds(new Set());
      setIsBulkMode(false); // Opcional: sair do modo de edição em massa após salvar
    } catch (error) {
      console.error('Erro ao salvar descrições em massa:', error);
      setMessage({ text: error.error || 'Falha ao atualizar descrições em massa.', type: 'error' });
    } finally {
      setLoading(false);
      setTimeout(() => setMessage({ text: '', type: '' }), 7000);
    }
  };

  if (error && !products.length) return <div className="error">Erro: {error}</div>; // Mostrar erro apenas se não houver produtos
  // Não mostrar 'Carregando produtos...' se já houver produtos na tela e estiver apenas atualizando
  // if (loading && !products.length) return <div className="loading">Carregando produtos...</div>;
  if (error) return <div className="error">Erro: {error}</div>;

  return (
    <div className="product-edit-container">
      <div className="product-edit-header">
        <form onSubmit={handleSearchSubmit} className="search-form">
          <input
            type="text"
            placeholder="Buscar produtos por nome ou código..."
            value={searchTerm}
            onChange={handleSearchChange}
            className="search-input"
            disabled={loading || syncing}
          />
          <button type="submit" className="search-button" disabled={loading || syncing}>
            {loading && !syncing ? 'Buscando...' : 'Buscar'}
          </button>
        </form>
        <div className="admin-actions">
          <button 
            className="btn-sync" 
            onClick={handleSyncWithSqlServer}
            disabled={syncing || isBulkMode} // Desabilitar sync em modo de edição em massa
          >
            {syncing ? 'Sincronizando...' : 'Sincronizar com SQL Server'}
          </button>
          <button 
            className={`btn-bulk-edit ${isBulkMode ? 'active' : ''}`}
            onClick={toggleBulkMode}
            disabled={syncing || editingProduct}
          >
            {isBulkMode ? 'Cancelar Edição em Massa' : 'Editar Descrições em Massa'}
          </button>
          
          {isBulkMode && (
            <>
              <div className="bulk-selection-status">
                <span className="selection-count">{selectedProductIds.size} produto(s) selecionado(s)</span>
              </div>
              
              <button 
                className="btn-select-all"
                onClick={handleSelectAllToggle}
                disabled={loading || syncing}
              >
                {selectedProductIds.size === filteredProducts.length && filteredProducts.length > 0 ? 
                  'Desmarcar Todos' : 'Selecionar Todos'}
              </button>
              
              <button
                onClick={handleGenerateAIDescriptionsForSelected}
                disabled={loading || syncing || generatingAIDescriptions || selectedProductIds.size === 0}
                className="btn-bulk-action btn-ai-generate"
                style={{ marginLeft: '10px' }}
              >
                {generatingAIDescriptions ? (
                  <>
                    <span className="loading-spinner"></span>
                    Gerando Descrições IA...
                  </>
                ) : (
                  'Gerar Descrições IA'
                )}
              </button>
              
              {selectedProductIds.size > 0 && (
                <button 
                  className="btn-save-bulk"
                  onClick={handleSaveBulkDescriptions}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span className="loading-spinner"></span>
                      Salvando...
                    </>
                  ) : (
                    `Salvar ${selectedProductIds.size} Descrições`
                  )}
                </button>
              )}
            </>
          )}
        </div>
      </div>
      
      {message.text && (
        <div className={`message ${message.type}`}>
          {message.text}
        </div>
      )}

      {editingProduct ? (
        <ProductEditForm 
          product={editingProduct} 
          onSave={handleUpdateProduct}
          onCancel={handleCancelEdit}
        />
      ) : (
        <>
          <div className="product-search">
            <input 
              type="text" 
              placeholder="Buscar produtos por nome ou código..." 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          
          {loading ? (
            <div className="loading">Carregando produtos...</div>
          ) : error ? (
            <div className="error">Erro ao carregar produtos: {error}</div>
          ) : (
            <div className="products-list">
              <div className="products-header">
                {isBulkMode && (
                  <div className="col-select">
                    <input 
                      type="checkbox" 
                      checked={selectedProductIds.size === filteredProducts.length && filteredProducts.length > 0}
                      onChange={handleSelectAllToggle}
                      title="Selecionar todos os produtos"
                    />
                  </div>
                )}
                <div className="col-code">Código</div>
                <div className="col-name">Nome</div>
                <div className="col-tag">Marca</div>
                <div className="col-category">Categoria</div>
                <div className="col-price">Preço</div>
                <div className="col-sale-price">Preço Promocional</div>
                <div className="col-stock">Estoque</div>
                <div className="col-description">Descrição</div>
                <div className="col-active">Ativo</div>
                <div className="col-actions">Ações</div>
              </div>
              
              {filteredProducts.length === 0 ? (
                <div className="no-products">Nenhum produto encontrado.</div>
              ) : (
                <>
                  {filteredProducts.map(product => (
                    isBulkMode ? (
                      <div key={product.id || product.code} className="product-row-bulk-edit">
                        <div className="col-select">
                          <input 
                            type="checkbox" 
                            checked={selectedProductIds.has(product.id)}
                            onChange={() => handleProductSelectToggle(product.id)}
                          />
                        </div>
                        <div className="col-code" data-label="Código">{product.code}</div>
                        <div className="col-name" data-label="Nome" title={product.name}>{product.name}</div>
                        <div className="col-tag" data-label="Marca">{product.tag}</div>
                        <div className="col-category" data-label="Categoria">{product.category}</div>
                        <div className="col-price" data-label="Preço">{product.price ? parseFloat(product.price).toFixed(2) : 'N/A'}</div>
                        <div className="col-sale-price" data-label="Preço Promocional">{product.salePrice ? parseFloat(product.salePrice).toFixed(2) : 'N/A'}</div>
                        <div className="col-stock" data-label="Estoque">{product.stock}</div>
                        <div className="col-description" data-label="Descrição">
                          {selectedProductIds.has(product.id) ? (
                            <textarea 
                              value={editableDescriptions.get(product.id) || ''}
                              onChange={(e) => handleDescriptionChange(product.id, e.target.value)}
                              rows={3}
                              className="bulk-description-textarea"
                            />
                          ) : (
                            <span title={editableDescriptions.get(product.id) || product.description}>
                              {(editableDescriptions.get(product.id) || product.description)?.substring(0,30) + ((editableDescriptions.get(product.id) || product.description)?.length > 30 ? '...' : '')}
                            </span>
                          )}
                        </div>
                        <div className="col-active">{product.active ? 'Sim' : 'Não'}</div>
                        <div className="col-actions">
                          <button onClick={() => handleEditClick(product)} disabled={isBulkMode} className="btn-edit-single">
                            Editar
                          </button>
                        </div>
                      </div>
                    ) : (
                      <ProductEditItem 
                        key={product.id || product.code} 
                        product={product} 
                        onEdit={() => handleEditClick(product)}
                      />
                    )
                  ))}
                  
                  {/* Controles de paginação */}
                  <div className="pagination-controls">
                    <div className="pagination-info">
                      Mostrando {filteredProducts.length} de {totalProducts} produtos | Página {currentPage} de {totalPages}
                    </div>
                    <div className="pagination-buttons">
                      <button 
                        onClick={() => fetchProducts(1)}
                        disabled={currentPage === 1 || loading}
                        className="pagination-button"
                      >
                        &laquo; Primeira
                      </button>
                      <button 
                        onClick={() => fetchProducts(currentPage - 1)}
                        disabled={currentPage === 1 || loading}
                        className="pagination-button"
                      >
                        &lsaquo; Anterior
                      </button>
                      <button 
                        onClick={() => fetchProducts(currentPage + 1)}
                        disabled={currentPage >= totalPages || loading}
                        className="pagination-button"
                      >
                        Próxima &rsaquo;
                      </button>
                      <button 
                        onClick={() => fetchProducts(totalPages)}
                        disabled={currentPage >= totalPages || loading}
                        className="pagination-button"
                      >
                        Última &raquo;
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ProductEditList;
